import React, { useEffect, useState } from 'react';
import {
  Typography, Table, Button, Modal, Form, Select, InputNumber, Space, Tag, Popconfirm, message,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, MinusCircleOutlined } from '@ant-design/icons';
import api from '../api';

// Admin > Business Config > Shift Department Eligibility (admin only, Shift
// Board phase 1): for each Paycor shift department, the Team + Job pairs whose
// people may take its shifts, and the board's minimum overlap in minutes
// (routes/shift_board.py).
export default function ShiftDepartmentEligibilityPage() {
  const [data, setData]       = useState({ rows: [], departments: [], teams: [], jobs: [], min_minutes: 60 });
  const [loading, setLoading] = useState(true);
  const [minutes, setMinutes] = useState(60);
  const [modal, setModal]     = useState(null);   // null | { row }
  const [saving, setSaving]   = useState(false);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    api.get('/api/shift-board/eligibility')
      .then((r) => { setData(r.data); setMinutes(r.data.min_minutes); })
      .catch(() => message.error('Failed to load.'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const saveMinutes = async () => {
    try { await api.put('/api/shift-board/settings', { min_minutes: minutes }); message.success('Minimum minutes saved.'); load(); }
    catch (err) { message.error(err.response?.data?.error || 'Save failed.'); }
  };

  useEffect(() => {
    if (!modal) return;
    form.resetFields();
    form.setFieldsValue({
      department: modal.row?.Department,
      pairs: modal.row ? modal.row.Pairs.map((p) => ({ team_id: p.TeamID, job_id: p.JobID })) : [{}],
    });
  }, [modal, form]);

  const put = (department, pairs) => api.put('/api/shift-board/eligibility', { department, pairs });
  const save = async () => {
    let v;
    try { v = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      await put(v.department, (v.pairs || []).filter((p) => p && p.team_id && p.job_id));
      // Renamed to another department: the old one's pairs go.
      if (modal.row && modal.row.Department !== v.department) await put(modal.row.Department, []);
      setModal(null);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };
  const remove = async (row) => {
    try { await put(row.Department, []); load(); }
    catch (err) { message.error(err.response?.data?.error || 'Delete failed.'); }
  };

  const used = new Set(data.rows.map((r) => r.Department));
  const deptOptions = data.departments
    .filter((d) => !used.has(d) || d === modal?.row?.Department)
    .map((d) => ({ value: d, label: d }));
  const keptTeams = (modal?.row?.Pairs || []).map((p) => p.TeamID);
  const keptJobs = (modal?.row?.Pairs || []).map((p) => p.JobID);
  const teamOptions = data.teams.filter((t) => t.Status === 'active' || keptTeams.includes(t.ID))
    .map((t) => ({ value: t.ID, label: t.Name }));
  const jobOptions = data.jobs.filter((j) => j.IsActive || keptJobs.includes(j.JobID))
    .map((j) => ({ value: j.JobID, label: j.JobName }));

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Shift Department Eligibility</Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModal({ row: null })}>Add Shift Department</Button>
      </div>
      <Space align="center" style={{ marginBottom: 16 }} wrap>
        <Typography.Text>Minimum minutes of a shift an absence must cover:</Typography.Text>
        <InputNumber min={0} max={1440} precision={0} value={minutes} onChange={(v) => setMinutes(v ?? 0)} style={{ width: 100 }} />
        <Button onClick={saveMinutes} disabled={minutes === data.min_minutes}>Save</Button>
      </Space>
      <Table
        rowKey="Department"
        dataSource={data.rows}
        loading={loading}
        size="small"
        pagination={false}
        locale={{ emptyText: 'No shift departments set up yet.' }}
        columns={[
          { title: 'Shift Department', dataIndex: 'Department', key: 'Department', width: 240,
            sorter: (a, b) => a.Department.localeCompare(b.Department) },
          { title: 'Who may take its shifts (Team / Job)', dataIndex: 'Pairs', key: 'Pairs',
            render: (v) => (
              <Space size={[4, 4]} wrap>
                {v.map((p) => <Tag key={`${p.TeamID}|${p.JobID}`}>{p.TeamName} / {p.JobName}</Tag>)}
              </Space>
            ) },
          { title: '', key: 'actions', width: 120, align: 'right',
            render: (_, row) => (
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => setModal({ row })}>Edit</Button>
                <Popconfirm title={`Remove ${row.Department}?`} okText="Remove" okButtonProps={{ danger: true }}
                  onConfirm={() => remove(row)}>
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ) },
        ]}
      />

      <Modal maskClosable={false} title={modal?.row ? 'Edit Shift Department' : 'Add Shift Department'}
        open={!!modal} onOk={save} onCancel={() => setModal(null)} confirmLoading={saving} okText="Save"
        width={620} destroyOnClose>
        <Form form={form} layout="vertical" preserve={false} style={{ marginTop: 12 }}>
          <Form.Item label="Shift Department" name="department" rules={[{ required: true, message: 'Pick a shift department' }]}>
            <Select showSearch optionFilterProp="label" options={deptOptions} />
          </Form.Item>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>Who may take its shifts</Typography.Text>
          <Form.List name="pairs" rules={[{ validator: async (_, v) => {
            if (!(v || []).some((p) => p && p.team_id && p.job_id)) throw new Error('Add at least one team and job');
          } }]}>
            {(fields, { add, remove: drop }, { errors }) => (
              <>
                {fields.map((f) => (
                  <Space key={f.key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    <Form.Item name={[f.name, 'team_id']} rules={[{ required: true, message: 'Pick a team' }]} style={{ marginBottom: 0 }}>
                      <Select placeholder="Team" showSearch optionFilterProp="label" style={{ width: 240 }} options={teamOptions} />
                    </Form.Item>
                    <Form.Item name={[f.name, 'job_id']} rules={[{ required: true, message: 'Pick a job' }]} style={{ marginBottom: 0 }}>
                      <Select placeholder="Job" showSearch optionFilterProp="label" style={{ width: 220 }} options={jobOptions} />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => drop(f.name)} />
                  </Space>
                ))}
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({})}>Add Team / Job</Button>
                <Form.ErrorList errors={errors} />
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </>
  );
}
